import type { UseFormSetValue } from "react-hook-form";
import type { GoogleProfileFormData } from "@/lib/formValidation";
import type { ReferralPartnerSummary } from "@/lib/types";
import { bindDevToolboxScenarioIdListener } from "@/lib/devToolbox/bindScenarioListener";
import { isDevToolboxEnabled } from "@/lib/devToolbox/isDevToolboxEnabled";
import type { DevToolboxScenarioId } from "@/lib/devToolbox/types";

export type GoogleProfileDevToolboxContext = {
  setValue: UseFormSetValue<GoogleProfileFormData>;
  setMissingFields: (fields: string[]) => void;
  setLocationValue: (value: string) => void;
  setResolvedPartner: (partner: ReferralPartnerSummary | null) => void;
  setPartnerLookupError: (error: string | null) => void;
  setIsPartnerLocked: (locked: boolean) => void;
  setLockedReferralCode: (code: string | null) => void;
};

export function isGoogleProfileDevToolboxPreviewActive(
  scenarioId: DevToolboxScenarioId | null,
): boolean {
  return scenarioId?.startsWith("google-profile.") ?? false;
}

export function shouldBypassAuthForGoogleProfilePreview(): boolean {
  if (!isDevToolboxEnabled() || typeof window === "undefined") return false;
  const email = new URLSearchParams(window.location.search).get("email");
  return email === "dev@sellitoff.local";
}

export function applyDevToolboxScenario(
  scenarioId: DevToolboxScenarioId | null,
  context: GoogleProfileDevToolboxContext,
): (() => void) | void {
  if (!scenarioId) return;

  const {
    setValue,
    setMissingFields,
    setLocationValue,
    setResolvedPartner,
    setPartnerLookupError,
    setIsPartnerLocked,
    setLockedReferralCode,
  } = context;

  if (scenarioId === "google-profile.empty-required") {
    const timer = window.setTimeout(() => {
      setMissingFields([
        "First Name",
        "Last Name",
        "Phone Number",
        "Gender",
        "Location",
      ]);
      setValue("firstName", "", { shouldValidate: true });
      setValue("lastName", "", { shouldValidate: true });
      setValue("phoneNumber", "", { shouldValidate: true });
      setValue("gender", "", { shouldValidate: true });
      setValue("location", "", { shouldValidate: true });
      setLocationValue("");
      setValue("discoveryChannel", "market_partner", { shouldValidate: true });
      setValue("partnerReferralCode", "INVALIDCODE1", { shouldValidate: true });
      setResolvedPartner(null);
      setPartnerLookupError("We couldn't find that market partner code");
      setIsPartnerLocked(false);
      setLockedReferralCode(null);
    }, 0);
    return () => window.clearTimeout(timer);
  }

  if (scenarioId === "google-profile.prefilled-incomplete") {
    const timer = window.setTimeout(() => {
      setMissingFields(["Location"]);
      setValue("firstName", "Demo", { shouldValidate: true });
      setValue("lastName", "User", { shouldValidate: true });
      setValue("phoneNumber", "2348012345678", { shouldValidate: true });
      setValue("gender", "Female", { shouldValidate: true });
      setValue("location", "", { shouldValidate: true });
      setLocationValue("");
      setValue("discoveryChannel", "market_partner", { shouldValidate: true });
      setValue("partnerReferralCode", "VALIDPARTNER1", {
        shouldValidate: true,
      });
      setResolvedPartner({
        id: "dev-partner-001",
        name: "Demo Market Partner",
      });
      setPartnerLookupError(null);
      setIsPartnerLocked(false);
      setLockedReferralCode(null);
    }, 0);
    return () => window.clearTimeout(timer);
  }

  if (scenarioId === "google-profile.success-preview") {
    const timer = window.setTimeout(() => {
      setMissingFields([]);
      setValue("firstName", "Demo", { shouldValidate: true });
      setValue("lastName", "User", { shouldValidate: true });
      setValue("phoneNumber", "2348012345678", { shouldValidate: true });
      setValue("gender", "Male", { shouldValidate: true });
      setValue("location", "Lagos, Ikeja", { shouldValidate: true });
      setLocationValue("Lagos, Ikeja");
      setValue("discoveryChannel", "market_partner", { shouldValidate: true });
      setValue("partnerReferralCode", "VALIDPARTNER1", {
        shouldValidate: true,
      });
      setResolvedPartner({
        id: "dev-partner-001",
        name: "Demo Market Partner",
      });
      setPartnerLookupError(null);
      setIsPartnerLocked(false);
      setLockedReferralCode(null);
    }, 0);
    return () => window.clearTimeout(timer);
  }
}

export function subscribeGoogleProfileDevToolboxAdapter(
  context: GoogleProfileDevToolboxContext,
): () => void {
  if (!isDevToolboxEnabled()) return () => {};

  let cleanup: (() => void) | void;

  const unsubscribe = bindDevToolboxScenarioIdListener((scenarioId) => {
    cleanup?.();
    cleanup = applyDevToolboxScenario(scenarioId, context);
  });

  return () => {
    cleanup?.();
    unsubscribe();
  };
}
